import * as Dialog from '@radix-ui/react-dialog'
import { X, ExternalLink, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { MapView } from '@/components/map/MapView'
import { LocationDetails } from '@/components/maps/LocationDetails'
import type { Camera } from '@/types'

interface CameraMapModalProps {
  camera: Camera | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CameraMapModal({ camera, open, onOpenChange }: CameraMapModalProps) {
  const { t } = useTranslation('cameras')

  if (!camera || !camera.latitude || !camera.longitude) return null

  const lat = parseFloat(camera.latitude)
  const lng = parseFloat(camera.longitude)
  const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`

  const marker = {
    id: camera.id,
    lat,
    lng,
    label: camera.name,
    sublabel: camera.location ?? undefined,
    isOnline: camera.is_online,
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />

        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Dialog.Title className="sr-only">{t('mapModal.title', { name: camera.name })}</Dialog.Title>
          <Dialog.Description className="sr-only">
            {camera.location ?? `${lat.toFixed(6)}, ${lng.toFixed(6)}`}
          </Dialog.Description>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 flex-shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                <MapPin className="h-3.5 w-3.5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{camera.name}</p>
                {camera.location && (
                  <p className="text-xs text-muted-foreground truncate">{camera.location}</p>
                )}
              </div>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Map — fixed height */}
          <div className="h-[420px] w-full">
            <MapView
              markers={[marker]}
              center={{ lat, lng }}
              zoom={18}
              className="h-full w-full"
              streetViewSwitch={{ lat, lng }}
            />
          </div>

          {/* Footer */}
          {/* #325 — the address wraps, the button keeps its size */}
          <div className="flex items-start justify-between gap-3 px-4 py-3 border-t border-border/60 flex-shrink-0 bg-muted/20">
            {/* #247 — address, Plus Code, coordinates, directions (the button keeps Open in Google Maps) */}
            <LocationDetails lat={lat} lng={lng} address={camera.location_address} openInMaps={false} className="min-w-0 flex-1" />
            <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
                <ExternalLink className="h-3.5 w-3.5" />
                {t('map.openInMaps')}
              </Button>
            </a>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
